"use client"

import { useEffect, useRef, useState } from "react"
import { Button } from "../ui/button"
import { Check } from "lucide-react"
import { dictionary } from '../../utils/dictionary';
import { useAuth } from "../../context/AuthContext";

interface Avatar {
  id: number
  avatar_url?: string
  gender: string
}

interface AvatarSelectorProps {
  avatars: Avatar[]
  onSelectAvatar?: (avatar: Avatar) => void
  setOpenVideoModal: (open: boolean) => void
}

export function AvatarSelector({ avatars, onSelectAvatar, setOpenVideoModal }: AvatarSelectorProps) {
  const [selectedAvatar, setSelectedAvatar] = useState<Avatar | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const { language } = useAuth();
  const modalRef = useRef<HTMLDivElement | null>(null)

  const handleAvatarSelect = (avatar: Avatar) => {
    setSelectedAvatar(avatar)
  }

  const handleCreateWithAvatar = () => {
    if (selectedAvatar && onSelectAvatar) {
      onSelectAvatar(selectedAvatar)
    }
  }

  useEffect(() => {
    setIsOpen(true)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) // si clickeo fuera del modal
      ) {
        if (isOpen) {
          setOpenVideoModal(false)
          setIsOpen(false)
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen, setIsOpen, setOpenVideoModal])

  return (
    <div ref={modalRef} className="flex flex-col h-[70vh]">
      {/* Grid de avatares con scroll */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="grid grid-cols-2 gap-4">
          {avatars.map((avatar) => (
            <div
              key={avatar.id}
              onClick={() => handleAvatarSelect(avatar)}
              className={`
                relative cursor-pointer group transition-all duration-200 
                rounded-xl border-2 p-3 hover:shadow-lg hover:scale-105
                ${selectedAvatar?.id === avatar.id
                  ? "border-primary bg-primary/5 shadow-md"
                  : "border-border hover:border-primary/50"
                }
              `}
            >
              {/* Indicador de selección */}
              {selectedAvatar?.id === avatar.id && (
                <div className="absolute -top-2 -right-2 z-10">
                  <div className="bg-primary text-primary-foreground rounded-full p-1">
                    <Check className="h-4 w-4" />
                  </div>
                </div>
              )}

              {/* Avatar */}
              <div className="aspect-square mb-3 overflow-hidden rounded-lg bg-muted/20">
                <img
                  className="w-full h-full object-contain transition-transform group-hover:scale-105 rounded-lg"
                  src={avatar.avatar_url || "/placeholder.svg?height=200&width=200"}
                  alt={`Avatar #${avatar.id}`}
                  crossOrigin="anonymous"
                  loading="lazy"
                />
              </div>
              

              {/* Info del avatar */}
              <div className="text-center space-y-1">
                <p className="text-sm font-medium capitalize text-foreground">{dictionary[avatar.gender][language]}</p>
                <span className="text-xs text-muted-foreground">Avatar #{avatar.id}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Botón de acción */}
      <div className="border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 p-4">
        <Button onClick={handleCreateWithAvatar} disabled={!selectedAvatar} className="w-full" size="lg">
          {selectedAvatar ? `Crear con Avatar #${selectedAvatar.id}` : "Selecciona un avatar"}
        </Button>

        {selectedAvatar && (
          <p className="text-center text-sm text-muted-foreground mt-2">
            Avatar seleccionado: {selectedAvatar.gender} #{selectedAvatar.id}
          </p>
        )}
      </div>
    </div>
  )
}
